import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type Message,
  MessagingWhatsappMessagesService,
} from 'src/api/generated';
import { refreshWhatsAppAvailability } from 'src/modules/shared/utils/integrations/refresh-whatsapp-availability.util';
import {
  createOptimisticMessage,
  withClientMessageId,
} from '../../utils/optimistic-whatsapp-message.util';
import type { WhatsAppMessageContext } from '../../types/whatsapp-message-context.type';
import type {
  ExtendedWhatsAppTemplateMessageParams,
  UseWhatsAppTemplateMessageCreateResponse,
  WhatsAppTemplateMutationCallbacks,
} from '../../types/whatsapp-template-send.types';

/**
 * Performs a neutral template request and exposes per-submission lifecycle hooks.
 * @param callbacks - Optional consumer-owned cache and presentation effects.
 * @returns Request actions and state, shared by standalone and CRM consumers.
 */
export const useCreateWhatsAppTemplateMessage = <TState = undefined>(
  callbacks: WhatsAppTemplateMutationCallbacks<TState> = {}
): UseWhatsAppTemplateMessageCreateResponse => {
  const createWhatsAppTemplateMessage = async (
    params: ExtendedWhatsAppTemplateMessageParams
  ): Promise<Message> => {
    const { optimisticData: _optimisticData, ...rest } = params;
    return await MessagingWhatsappMessagesService.createTemplateMessage(rest);
  };

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: createWhatsAppTemplateMessage,
    onMutate: (params) => {
      const optimisticMessage = createOptimisticMessage({
        content: params.optimisticData?.content ?? params.templateName,
        fromPhoneNumber: params.fromPhoneNumber,
        toPhoneNumber: params.toPhoneNumber,
        customFields: params.customFields,
        optimisticData: params.optimisticData,
      });

      const context: WhatsAppMessageContext = {
        queryClient,
        params,
        optimisticMessage,
      };
      return { context, state: callbacks.onMutate?.(context) };
    },
    onSuccess: (data, _params, mutationContext) => {
      callbacks.onSuccess?.(
        data,
        mutationContext.context,
        mutationContext.state
      );
    },
    onError: (error: Error, params, mutationContext) => {
      refreshWhatsAppAvailability(queryClient);
      callbacks.onError?.(
        error,
        mutationContext?.context ?? { queryClient, params },
        mutationContext?.state
      );
    },
    onSettled: (_data, _error, params, mutationContext) => {
      callbacks.onSettled?.(
        mutationContext?.context ?? { queryClient, params },
        mutationContext?.state
      );
    },
  });

  return {
    loading: mutation.isPending,
    error: mutation.error,
    message: mutation.data,
    createWhatsAppTemplateMessage: (params) =>
      mutation.mutate(withClientMessageId(params)),
    createWhatsAppTemplateMessageAsync: (params) =>
      mutation.mutateAsync(withClientMessageId(params)),
  };
};
